// When time entries close — the frontend's copy of the rule.
//
// A week's entries lock at the END of its GRACE DAY, through that week's
// Friday. The grace day is the first working day after the week: normally the
// following Monday, slid forwards over bank holidays. Once it ends, the whole
// week is closed.
//
// This MIRRORS src/lib/timeLock.js, which is authoritative: the server refuses
// the write, and this copy exists only so the screen can grey out what would be
// refused rather than letting somebody type an entry and then be turned away.
// The two must stay identical, and tests/time-entries-monday-lock.test.mjs
// imports both and compares them day by day across two years, with and without
// holidays, so a change to one without the other fails the build rather than
// quietly showing people controls that do not work.
//
// Callers supply isHoliday from the bank-holidays feed (kind 'bank_holiday'
// rows only, matching the server's bank_holidays table). Omitting it falls
// back to the plain-Monday rule, which never locks LATER than the real rule,
// so a failed fetch can only show a padlock the server would in fact accept —
// never the reverse.
//
// The duplication is deliberate: the Worker and the Vite app are separate
// bundles with no shared module boundary between them, and inventing one for a
// dozen lines would be a bigger change than the rule itself.

const DAY = 86400000;

const shift = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d)) return iso;
  return new Date(d.getTime() + days * DAY).toISOString().slice(0, 10);
};

/** The grace day for the week ending this Friday: the following Monday, slid
 *  forwards over bank holidays. Capped at that week's Friday for termination. */
export function graceDay(fridayISO, isHoliday = () => false) {
  let day = shift(fridayISO, 3);
  for (let hops = 0; hops < 4 && isHoliday(day); hops++) day = shift(day, 1);
  return day;
}

/** The last date that is closed, given today's date. Inclusive. */
export function lockedThrough(todayISO, isHoliday = () => false) {
  const d = new Date(`${todayISO}T00:00:00Z`);
  if (isNaN(d)) return '';
  // Days back to the most recent Tuesday (the first day a plain-Monday lock is
  // in force), then four more to the Friday it locked.
  const back = ((d.getUTCDay() - 2 + 7) % 7) + 4;
  let friday = shift(todayISO, -back);
  while (graceDay(friday, isHoliday) >= todayISO) friday = shift(friday, -7);
  return friday;
}

/** The earliest date that may still be added or amended. */
export function earliestEditable(todayISO, isHoliday = () => false) {
  const through = lockedThrough(todayISO, isHoliday);
  return through ? shift(through, 1) : '';
}

/** Is an entry on this date closed to add, amend and delete? */
export function isTimeEntryLocked(dateISO, todayISO, isHoliday = () => false) {
  if (!dateISO) return false;
  const through = lockedThrough(todayISO, isHoliday);
  return !!through && dateISO <= through;
}
